import {
  EdiOutboundPayloadDestination,
  InvoiceTransmissionMethod,
  TenediReadinessSection,
} from "../../generated/graphql";

/**
 * Every label and helper text on the Tenedi connections page, in one module so
 * the wording gets reviewed in one place (plan section 2.3). Labels name the
 * thing in operations terms; helper text says what a raw id is and where it
 * comes from.
 */
export type FieldCopy = {
  readonly label: string;
  readonly helper?: string;
};

export const CARRIER_FIELD_COPY = {
  isaId: {
    label: "Carrier EDI ID (ISA)",
    helper:
      "The ID this carrier uses on every EDI message. Your EDI contact at the forwarder or Tenedi gives you this. Usually 2 to 15 letters and digits.",
  },
  alternateIsaId: {
    label: "Other carrier EDI ID 1",
    helper: "Only if the forwarder sends to this carrier under a second ID.",
  },
  secondAlternateIsaId: { label: "Other carrier EDI ID 2" },
  thirdAlternateIsaId: { label: "Other carrier EDI ID 3" },
  fourthAlternateIsaId: { label: "Other carrier EDI ID 4" },
  standardCarrierAlphaCode: {
    label: "Carrier SCAC",
    helper:
      "The carrier's 2 to 4 letter code. Status and invoice messages cannot go out without it.",
  },
  alternateStandardCarrierAlphaCode: {
    label: "Other SCAC 1",
    helper:
      "Only if the carrier also trades under another SCAC. A partnership can use any of these.",
  },
  secondAlternateScac: { label: "Other SCAC 2" },
  thirdAlternateScac: { label: "Other SCAC 3" },
  fourthAlternateScac: { label: "Other SCAC 4" },
  stediPartnerId: {
    label: "Carrier Stedi partner ID",
    helper:
      "Still needed for Tenedi connections until the TMS stops checking it. Engineering or the Stedi account holds it.",
  },
} as const satisfies Record<string, FieldCopy>;

export const CUSTOMER_FIELD_COPY = {
  isaId: {
    label: "Customer EDI ID (ISA)",
    helper:
      "The forwarder's EDI ID. Tenedi uses it to pick this customer's message flow, so it must match Tenedi exactly.",
  },
  ediApplicationId: {
    label: "Customer GS ID",
    helper:
      "Only needed when another customer of this carrier shares the same EDI ID. The forwarder's EDI team gives you this.",
  },
  ediContactCode: {
    label: "Customer contact code",
    helper:
      "Splits one forwarder into several customers. Leave blank unless told otherwise.",
  },
  stediPartnerId: {
    label: "Customer Stedi partner ID",
    helper:
      "Still needed for Tenedi connections until the TMS stops checking it. Engineering or the Stedi account holds it.",
  },
  standardCarrierAlphaCode: {
    label: "Customer SCAC",
    helper:
      "The forwarder's own SCAC, if it has one. Sent in the message body.",
  },
  defaultInvoiceTransmissionMethod: {
    label: "Invoice method",
    helper:
      "EDI sends invoices (210) to the customer through this connection. Other methods send none.",
  },
} as const satisfies Record<string, FieldCopy>;

export const PARTNERSHIP_FIELD_COPY = {
  companyStandardCarrierAlphaCode: {
    label: "Carrier SCAC for this partnership",
    helper:
      "The SCAC the forwarder sends tenders to. It must be one of the carrier's SCACs.",
  },
  ediOutboundPayloadDestination: {
    label: "Send messages to",
    helper: "Tenedi, Stedi, or Boomi. New partnerships use Tenedi.",
  },
  autoAcceptOrders: {
    label: "Accept tenders automatically",
    helper:
      "On: the TMS accepts each tender as it arrives and sends the 990 then. Off: someone accepts or rejects in the TMS, and the 990 goes out at that point.",
  },
  send990InResponseTo204: { label: "Reply with a 990 to each 204" },
  send990InResponseTo211: { label: "Reply with a 990 to each 211" },
  edi214ShipmentStatuses: {
    label: "Status updates (214) to send",
    helper:
      "Tick each status the forwarder wants. Nothing is sent for unticked statuses.",
  },
  canCreateMultipleStops: { label: "Tenders can have more than two stops" },
  ignoreCancellations: { label: "Ignore cancellation messages" },
  allowEditingShipperBillOfLadingNumber: {
    label: "Let staff edit the HAWB on EDI orders",
    helper: "The TMS keeps the original HAWB from the tender.",
  },
  ignoreNonEdiOutbound: {
    label: "Send messages only for orders that came in by EDI",
    helper: "Turn on when the forwarder cannot match orders it did not tender.",
  },
  removeSpacesFrom214Bol: { label: "Remove spaces from the BOL in 214s" },
  excludeZeroChargeInvoices: { label: "Skip invoices with a zero total" },
  sendBackupDocs: {
    label: "Send backup documents with invoices",
    helper: "Attaches PODs or BOLs to each invoice.",
  },
  alternateStediPartnerId: {
    label: "Alternate sender Stedi partner ID",
    helper:
      "Advanced. Replaces the carrier's Stedi partner ID on 214s for this partnership. Leave blank unless engineering asks.",
  },
  enableStaging: {
    label: "Allow sends from staging and demo",
    helper:
      "Lets new messages go out from staging and demo. Today a config bug sends from every environment whatever you set here (TC-1154). Once that is fixed, turn this on for a partnership you test from staging. Stedi resends ignore it.",
  },
  useStediGenerateEdiFunction: {
    label: "Use the newer Stedi message format",
    helper:
      "Off for Tenedi partnerships until Tenedi confirms which format it reads.",
  },
} as const satisfies Record<string, FieldCopy>;

export const PRODUCTION_SAVE_HINT = "Type prod in the banner above to save.";

export const DESTINATION_LABELS: Record<EdiOutboundPayloadDestination, string> =
  {
    [EdiOutboundPayloadDestination.Tenedi]: "Tenedi",
    [EdiOutboundPayloadDestination.Stedi]: "Stedi",
    [EdiOutboundPayloadDestination.Boomi]: "Boomi",
  };

export const INVOICE_METHOD_LABELS: Record<InvoiceTransmissionMethod, string> =
  {
    [InvoiceTransmissionMethod.Edi]: "EDI (210)",
    [InvoiceTransmissionMethod.Email]: "Email",
    [InvoiceTransmissionMethod.Download]: "Download",
    [InvoiceTransmissionMethod.EmailAndDownload]: "Email and download",
    [InvoiceTransmissionMethod.None]: "None",
  };

export const READINESS_SECTION_LABELS: Record<TenediReadinessSection, string> =
  {
    [TenediReadinessSection.Carrier]: "Carrier identity",
    [TenediReadinessSection.Customer]: "Customer identity",
    [TenediReadinessSection.Partnership]: "Partnerships",
    [TenediReadinessSection.Environment]: "Engineering",
    [TenediReadinessSection.Tenedi]: "Tenedi",
  };

const FIELD_LABELS: Record<string, string> = Object.fromEntries(
  [CARRIER_FIELD_COPY, CUSTOMER_FIELD_COPY, PARTNERSHIP_FIELD_COPY].flatMap(
    (group) =>
      Object.entries(group).map(([field, copy]) => [field, copy.label]),
  ),
);

/**
 * The label for a field the server names in a change summary. Carrier and
 * customer share some field names; the summary shows which card saved, so the
 * shared label reads correctly in either.
 */
export const changeFieldLabel = (
  field: string,
  scope: "carrier" | "customer" | "partnership",
): string => {
  const scoped: Record<string, FieldCopy> =
    scope === "carrier"
      ? CARRIER_FIELD_COPY
      : scope === "customer"
        ? CUSTOMER_FIELD_COPY
        : PARTNERSHIP_FIELD_COPY;
  return scoped[field]?.label ?? FIELD_LABELS[field] ?? field;
};

/** How the change summary shows a raw value from the server. */
export const changeValueLabel = (value: string | null | undefined): string => {
  if (value === null || value === undefined || value === "") {
    return "(blank)";
  }
  if (value === "true") {
    return "On";
  }
  if (value === "false") {
    return "Off";
  }
  return value;
};
